import React, { CSSProperties, JSX, useRef, useState } from 'react'
import { ChatOverlayMockup, EmoteWallMockup } from 'components/overlayPreview/OverlayMockups'
import { usePublicHomePreviewSeam } from 'components/overlayPreview/usePreviewSeam'
import { PUBLIC_HOME_COPY as COPY } from 'lib/publicHomeCopy'
import { useElementWidth, useNearViewport, wallDesignSize } from 'components/publicHome/publicHomeHooks'
import { HERO_CHAT_CONFIG, HERO_SEED, HERO_WALL_CONFIG } from 'components/publicHome/publicHomeSamples'
import { FlightDeckGameBlurb, FlightDeckGameLauncher } from 'components/publicHome/FlightDeckGameLauncher'

const SCENE_STYLE: CSSProperties = {
  backgroundColor: '#0e1218',
  backgroundImage: [
    'radial-gradient(65% 70% at 22% 30%, rgba(19, 57, 88, 0.9), transparent 70%)',
    'radial-gradient(45% 55% at 70% 85%, rgba(132, 84, 62, 0.45), transparent 70%)',
    'radial-gradient(35% 40% at 85% 15%, rgba(252, 180, 21, 0.10), transparent 70%)',
    'linear-gradient(160deg, #1a2230 0%, #0c0f14 100%)'
  ].join(', ')
}

const HIDE_RENDERER_TAGS = '[&_[data-testid=overlay-sample-tag]]:hidden'

const WALL_BESIDE_CHAT = [
  'sm:[mask-image:linear-gradient(to_right,#000_calc(100%_-_max(40%,220px)_-_56px),transparent_calc(100%_-_max(40%,220px)))]',
  'sm:[-webkit-mask-image:linear-gradient(to_right,#000_calc(100%_-_max(40%,220px)_-_56px),transparent_calc(100%_-_max(40%,220px)))]'
].join(' ')

const CHAT_FADE = [
  '[mask-image:linear-gradient(to_bottom,transparent_0,transparent_32px,#000_72px)]',
  '[-webkit-mask-image:linear-gradient(to_bottom,transparent_0,transparent_32px,#000_72px)]',
  'sm:[mask-image:linear-gradient(to_bottom,transparent_0,transparent_15%,#000_40%)]',
  'sm:[-webkit-mask-image:linear-gradient(to_bottom,transparent_0,transparent_15%,#000_40%)]'
].join(' ')

export const HeroStage = ({ className = '' }: { className?: string }): JSX.Element => {
  const seam = usePublicHomePreviewSeam(HERO_SEED)
  const frameRef = useRef<HTMLDivElement | null>(null)
  const sceneRef = useRef<HTMLDivElement | null>(null)
  const near = useNearViewport(frameRef, { initial: true })
  const frameWidth = useElementWidth(sceneRef)
  const design = wallDesignSize(frameWidth)
  const [gameOpen, setGameOpen] = useState(false)

  return (
    <figure className={`mx-auto w-full max-w-5xl ${className}`}>
      <div
        ref={frameRef}
        className='relative w-full max-w-full overflow-hidden rounded-2xl border border-white/15 shadow-[0_2px_10px_1px_#0003]'
      >
        <div aria-hidden='true' className={`relative ${HIDE_RENDERER_TAGS}`}>
          <div ref={sceneRef} className='relative aspect-video w-full overflow-hidden' style={SCENE_STYLE}>
            <div data-testid='hero-wall-layer' className={`absolute inset-0 ${WALL_BESIDE_CHAT}`}>
              {near && frameWidth > 0 && !gameOpen && (
                <EmoteWallMockup
                  seam={seam}
                  config={HERO_WALL_CONFIG}
                  designWidth={design.width}
                  designHeight={design.height}
                />
              )}
            </div>
            {!gameOpen && (
              <span
                data-testid='hero-scene-label'
                className='pointer-events-none absolute bottom-2 left-2 z-20 rounded bg-black/70 px-1.5 py-0.5 text-xs leading-4 text-white/60'
              >
                {COPY.sceneLabel}
              </span>
            )}
          </div>
          <div
            data-testid='hero-chat-layer'
            className='relative z-10 h-60 w-full bg-[#0a0d12] sm:absolute sm:inset-y-0 sm:right-0 sm:h-auto sm:w-[40%] sm:min-w-[220px] sm:bg-transparent'
          >
            <div className={`absolute inset-0 px-3 ${CHAT_FADE}`}>
              {near && <ChatOverlayMockup seam={seam} config={HERO_CHAT_CONFIG} fillHost />}
            </div>
          </div>
        </div>

        <FlightDeckGameLauncher open={gameOpen} onOpenChange={setGameOpen} paused={!near} />
      </div>
      {gameOpen && <FlightDeckGameBlurb className='mt-3 sm:hidden' />}
      <figcaption className='mt-3 text-center text-xs leading-[18px] text-dm-text-accent sm:text-[13px] sm:leading-5'>
        {COPY.stageCaption}
      </figcaption>
    </figure>
  )
}
